<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { ComandaSplit } from '@/enums/ComandaSplit';
  import {
    buildComandaSplitPreview,
    type IComandaPreviewLine,
  } from '@/domain/items/comanda-split-preview';

  const props = withDefaults(
    defineProps<{
      mode: ComandaSplit;
      /** Name of the product being configured; empty for a new product. */
      itemName?: string;
      /** The product is excluded from every station: the layout applies to nothing right now. */
      muted?: boolean;
    }>(),
    { itemName: '', muted: false }
  );
  const proxy = getCurrentInstance()!.proxy;

  const preview = computed(() => buildComandaSplitPreview(props.mode));
  const productName = computed(
    () =>
      (props.itemName ?? '').trim() ||
      String(proxy.$t('item.comandaSplitPreview.thisProduct'))
  );

  function lineText(line: IComandaPreviewLine): string {
    const name = line.nameKey
      ? String(proxy.$t(line.nameKey))
      : productName.value;
    const suffix = line.suffix ? ` ${line.suffix}` : '';
    return `${line.quantity}x ${name}${suffix}`;
  }
</script>

<template>
  <div
    class="split-preview"
    :class="{ 'is-muted': muted }"
    role="group"
    :aria-label="$t('item.comandaSplitPreview.title')"
  >
    <span class="preview-title">{{ $t('item.comandaSplitPreview.title') }}</span>
    <div class="tickets" aria-hidden="true">
      <div
        v-for="(ticket, ticketIndex) in preview.tickets"
        :key="ticketIndex"
        class="ticket"
      >
        <div v-if="ticket.captionKey" class="ticket-caption">
          {{ $t(ticket.captionKey) }}
        </div>
        <div
          v-for="(line, lineIndex) in ticket.lines"
          :key="lineIndex"
          class="line"
          :class="{ 'is-product': line.nameKey === null }"
        >
          <span>{{ lineText(line) }}</span>
          <span v-if="line.noteKey" class="line-note">
            {{ $t(line.noteKey) }}
          </span>
        </div>
      </div>
    </div>
    <p class="result">{{ $t(preview.resultKey) }}</p>
  </div>
</template>

<style scoped lang="scss">
  .split-preview {
    // Margins, not flex gap: old POS hardware (Chrome < 84).
    @apply tw-flex tw-flex-col tw-rounded-xl tw-bg-canvas tw-p-3;
    &.is-muted {
      @apply tw-opacity-60;
    }
    .preview-title {
      @apply tw-text-xs tw-font-medium tw-text-neutral-600;
    }
    // 8px below the title; the wrapping tickets get 4px around each, absorbed
    // by the negative margins.
    .tickets {
      @apply tw--mx-1 tw--mb-1 tw-mt-1 tw-flex tw-flex-wrap;
    }
    .ticket {
      // Full width minus its own 4px margins.
      @apply tw-m-1 tw-min-w-[8rem] tw-max-w-[calc(100%-0.5rem)] tw-flex-none tw-break-words tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-px-3 tw-py-2 tw-text-xs;
    }
    .ticket-caption {
      @apply tw-mb-1 tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }
    .line {
      @apply tw-flex tw-flex-col tw-tabular-nums tw-text-neutral-500;
      &.is-product {
        @apply tw-font-semibold tw-text-neutral-900;
      }
    }
    .line-note {
      @apply tw-text-xs tw-font-normal tw-text-neutral-500;
    }
    .result {
      @apply tw-m-0 tw-mt-2 tw-text-xs tw-text-neutral-600;
    }
  }
</style>
